import { VeyaLayout, Section, H2, SubPageHero, OtherPages, CheckCard, useMeta } from "./VeyaShared";
import { LANDING_FAQ } from "./Veya";

const Steps = ({ items }: { items: [string, string][] }) => (
  <ol className="grid grid-cols-3 gap-6 mobile:grid-cols-1 tablet:grid-cols-1">
    {items.map(([t, d], i) => (
      <li key={t} className="rounded-2xl border border-border bg-card p-6">
        <span className="font-funnel text-3xl font-bold text-muted-foreground">0{i + 1}</span>
        <h3 className="mt-3 font-funnel text-xl font-bold">{t}</h3>
        <p className="mt-2 text-muted-foreground">{d}</p>
      </li>
    ))}
  </ol>
);

export const VeyaHowItWorks = () => {
  useMeta("How veya works - veya by qwip", "How you add a trip to veya, how it checks the pieces fit together, and how monitoring works.");
  return (
    <VeyaLayout>
      <SubPageHero eyebrow="How it works" title="Add the trip. veya lines it up." intro="veya looks at your bookings side by side, the way a well-organised friend would, and tells you what's worth a second look." />
      <Section className="pt-0">
        <Steps items={[
          ["Add your trip", "Share your booking confirmations and anything else veya should know, like who's travelling or when you'd rather not arrive. The exact way to share details is still being decided."],
          ["veya checks the fit", "It compares times, places and gaps between bookings: arrival against check-in, connection times, and how you get from one place to the next."],
          ["You get clear notes", "Each note says what veya noticed and what you might do about it. You decide what, if anything, to change."],
        ]} />
      </Section>
      <div className="bg-muted/40">
        <Section>
          <H2>And once you've checked it</H2>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            Plans change after you book. veya can keep an eye on relevant updates, like a schedule change or a new terminal, and tell you if something may affect the rest of your trip. Monitoring is in development.
          </p>
        </Section>
      </div>
      <OtherPages current="/veya-how-it-works" />
    </VeyaLayout>
  );
};

export const VeyaTripCheck = () => {
  useMeta("Trip checks - veya by qwip", "The kinds of gaps and timing issues veya may flag in a booked trip, with labelled examples.");
  return (
    <VeyaLayout>
      <SubPageHero eyebrow="Trip checks" title="The details that slip between bookings" intro="These are the kinds of things veya looks for. The examples below are illustrative, not real trips." />
      <Section className="grid grid-cols-2 gap-6 pt-0 mobile:grid-cols-1">
        <CheckCard title="Late arrival before check-in" observed="Flight lands 21:40. Hotel reception closes at 22:00." checked="When the trip was added" source="Your flight and hotel confirmations" />
        <CheckCard title="Tight connection on separate tickets" observed="65 minutes between flights, booked separately, different terminals." checked="When the trip was added" source="Your two flight confirmations" />
        <CheckCard title="No onward transport" observed="No transfer, train or car hire between the airport and your stay." checked="When the trip was added" source="Your booking details" />
        <CheckCard title="Dates that don't match" observed="Hotel starts the 14th, but your flight arrives early on the 15th local time." checked="When the trip was added" source="Your flight and hotel confirmations" />
      </Section>
      <Section className="pt-0">
        <H2>What a flag means</H2>
        <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
          A flag is something worth checking, not proof something is wrong. veya only sees what you share, so it may miss things or flag something you've already sorted.
        </p>
      </Section>
      <OtherPages current="/veya-trip-check" />
    </VeyaLayout>
  );
};

export const VeyaMonitoring = () => {
  useMeta("Monitoring - veya by qwip", "How veya may watch for flight, airport and transport changes that affect your trip, and what it can't predict.");
  return (
    <VeyaLayout>
      <SubPageHero eyebrow="Monitoring · In development" title="A heads-up when something shifts" intro="After you've booked, veya can watch for updates that may affect your plan, and tell you what it saw, when, and where it came from." />
      <Section className="pt-0">
        <ul className="grid grid-cols-3 gap-6 mobile:grid-cols-1 tablet:grid-cols-1">
          {[
            ["Flight changes", "Schedule, gate or terminal changes, from airline or airport status information where it's available."],
            ["Airport updates", "Changes that could affect your departure or arrival, such as terminal moves."],
            ["Recent delay patterns", "How a flight has arrived recently, where reliable published data exists."],
          ].map(([t, d]) => (
            <li key={t} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-funnel text-xl font-bold">{t}</h3>
              <p className="mt-2 text-muted-foreground">{d}</p>
            </li>
          ))}
        </ul>
      </Section>
      <div className="bg-muted/40">
        <Section>
          <H2>What monitoring can't do</H2>
          <ul className="mt-6 max-w-2xl list-disc space-y-3 pl-5 text-lg text-muted-foreground">
            <li>It can't predict delays. A pattern of late arrivals is a reason to leave more time, not a forecast.</li>
            <li>It depends on what's published. Some routes and operators share less data than others.</li>
            <li>It won't rebook or change anything. It tells you, and you decide.</li>
          </ul>
        </Section>
      </div>
      <OtherPages current="/veya-monitoring" />
    </VeyaLayout>
  );
};

const EXTRA_FAQ: [string, string][] = [
  ["Do I need to connect my email?", "No decision has been made on email connection. If we offer it, it will be optional and you'll be asked first."],
  ["Which booking sites will it work with?", "We're exploring how to support confirmations from different airlines, hotels and booking sites. We'll share specifics once they're confirmed."],
  ["Can veya make mistakes?", "Yes. It only knows what you share and what's published, so treat its notes as things to check rather than final answers."],
  ["Is veya available now?", "Not yet. It's in development. Join early access and we'll tell you when there's something to try."],
  ["Can I delete my information?", "Yes. Email hello@qwip.co.uk and we'll remove your early-access email."],
];

export const VeyaFaq = () => {
  useMeta("veya FAQ - veya by qwip", "Answers about what veya checks, sharing booking details, data access, privacy, and what veya can and can't do.");
  return (
    <VeyaLayout>
      <SubPageHero eyebrow="FAQ" title="Questions about veya" intro="Straight answers, including the parts we're still working out." />
      <Section className="pt-0">
        <div className="divide-y divide-border border-y border-border">
          {[...LANDING_FAQ, ...EXTRA_FAQ].map(([q, a]) => (
            <div key={q} className="py-6">
              <h2 className="font-funnel text-lg font-bold">{q}</h2>
              <p className="mt-2 max-w-3xl text-muted-foreground">{a}</p>
            </div>
          ))}
        </div>
      </Section>
      <OtherPages current="/veya-faq" />
    </VeyaLayout>
  );
};
